<template>
  <div class="BodyContent">
    <!-- <BlockContent v-bind="{ blocks, serializers }" /> -->
    <PortableText :value="blocks" :components="serializerComponents" />
  </div>
</template>

<script>
import { h, markRaw } from 'vue'

import CtaButton from '../../button-styles/CtaButton.vue'

import BodyContentInlineImage from './marks/InlineImage.vue'
import BodyContentLinkToPage from './marks/LinkToPage.vue'

import BodyContentCaution from './styles/Caution.vue'
import BodyContentNote from './styles/Note.vue'
import BodyContentTip from './styles/Tip.vue'

import BodyContentCodeBlock from './types/CodeBlock.vue'
import BodyContentImage from './types/Image.vue'
import BodyContentTable from './types/Table.vue'

const serializerComponents = {
  block: {
    'caution': markRaw(BodyContentCaution),
    'note': markRaw(BodyContentNote),
    'tip': markRaw(BodyContentTip),
  },
  types: {
    codeBlock: markRaw(BodyContentCodeBlock),
    image: markRaw(BodyContentImage),
    cta: ({ value }) => h(CtaButton, { text: value.text, action: value.action, class: 'ctaButton --dark-blue' }),
    table: markRaw(BodyContentTable),
  },
  marks: {

    // X link: ({ text, value }) => h('a', { href: value.href, target: value.blank && '_blank' }, text),
    // X actionLink: ({ text, value }) => h(UnstyledCta, { text, action: value.action, class: 'actionLink' }),

    inlineImage: markRaw(BodyContentInlineImage),
    link: ({ text, value }) => h('a', { href: value.href, target: value.blank && '_blank' }, text),
    linkToPage: markRaw(BodyContentLinkToPage),
  },
}

export default {
  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    useCheckIconBullets: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return { serializerComponents }
  },
}
</script>
<!--
<script>
import BodyContentInlineImage from './marks/InlineImage.vue'
import BodyContentTextLink from './marks/TextLink.vue'
import BodyContentLinkToPage from './marks/LinkToPage.vue'

import BodyContentCaution from './styles/Caution.vue'
import BodyContentNote from './styles/Note.vue'
import BodyContentTip from './styles/Tip.vue'

import BodyContentCodeBlock from './types/CodeBlock.vue'
import BodyContentCtaButton from './types/CtaButton.vue'
import BodyContentImage from './types/Image.vue'
import BodyContentTable from './types/Table.vue'

// Modified version of BlockTypeSerializer from sanity-blocks-vue-component/src/serializers.js
const BodyContentBlock = {

  name: 'BodyContentBlock',
  functional: true,

  render(createElement, { props, children }) {

    const style = props.style || 'normal'

    if (/^h\d/.test(style)) {
      return createElement(style, null, children)
    }

    switch (style) {
    case 'note':
      return createElement(BodyContentNote, null, children)
    case 'tip':
      return createElement(BodyContentTip, null, children)
    case 'caution':
      return createElement(BodyContentCaution, null, children)
    case 'blockquote':
      return createElement('blockquote', null, children)
    default:
      return createElement('p', null, children)
    }
  },
}

const serializers = {

  marks: {
    inlineImage: BodyContentInlineImage,
    link: BodyContentTextLink,
    linkToPage: BodyContentLinkToPage,
  },

  types: {
    block: BodyContentBlock,
    codeBlock: BodyContentCodeBlock,
    image: BodyContentImage,
    cta: BodyContentCtaButton,
    table: BodyContentTable,
  },
}

export default {
  props: {
    blocks: {
      type: Array,
      default: Array,
    },
  },
  data() {
    return {
      serializers,
    }
  },
}
</script> -->

<style scoped>

.BodyContent {
  font-size: var(--font-size-3);
  line-height: 1.75;
  color: var(--text-color-dark);
}

.BodyContent:deep(h1),
.BodyContent:deep(h2),
.BodyContent:deep(h3),
.BodyContent:deep(h4),
.BodyContent:deep(h5),
.BodyContent:deep(h6) {
  color: var(--text-color-black);
  font-weight: 600;
  line-height: 1.2;
}

.BodyContent:deep(h1:not(:first-child)),
.BodyContent:deep(h2:not(:first-child)),
.BodyContent:deep(h3:not(:first-child)),
.BodyContent:deep(h4:not(:first-child)),
.BodyContent:deep(h5:not(:first-child)),
.BodyContent:deep(h6:not(:first-child)) {
  margin-top: var(--spacing-10);
}

.BodyContent:deep(h1) {
  font-size: var(--font-size-6);
}
.BodyContent:deep(h2) {
  font-size: var(--font-size-5);
}
.BodyContent:deep(h3) {
  font-size: var(--font-size-4);
}
.BodyContent:deep(h4) {
  font-size: var(--font-size-3);
}
.BodyContent:deep(h5) {
  font-size: var(--font-size-2);
}
.BodyContent:deep(h6) {
  font-size: var(--font-size-1);
}

.BodyContent:deep(p),
.BodyContent:deep(ul),
.BodyContent:deep(ol) {
  margin: var(--spacing-7) 0;
}

.BodyContent:deep(strong) {
  font-weight: 600;
}

.BodyContent:deep(ul),
.BodyContent:deep(ol) {
  padding-left: 1.5rem;
  line-height: 1.5;
}
.BodyContent:deep(ul) {
  list-style: disc;
}
.BodyContent:deep(ol) {
  list-style: decimal;
}

.BodyContent:deep(ul li),
.BodyContent:deep(ol li) {
  margin: 1.5rem 0;
}

.BodyContent:deep(a) {
  text-decoration: underline;
}

.BodyContent:deep(a:hover) {
  color: var(--brand-color);
}

.BodyContent:deep(blockquote) {
  margin: var(--spacing-7) 0;
  padding: var(--spacing-2) 0;
  border-style: solid none;
  border-width: 2px;
  border-color: var(--hairline-color);
}

.BodyContent:deep(.BodyContentInfo) {
  font-weight: bold;
  color: #4f81bd;
}

.BodyContent:deep(.BodyContentAlert) {
  font-weight: bold;
  color: #c00000;
}

.BodyContent:deep(.BodyContentImage),
.BodyContent:deep(.BodyContentTable) {
  margin: var(--spacing-7) 0
}
</style>
